import { getAddresses, isAddressIncomplete } from '~/types/Address';
import { APIError } from '~/types/APIError';
import { getCustomsPhoneErrors } from '~/types/CustomsDeclaration';
import Order, { hasRecipientAddressError } from '~/types/Order';
import { BatchEntryExtended } from '~/types/OrderBatchEntry';
import { BatchEntryStatus } from '~/types/OrdersBatch';
import Rate from '~/types/Rate';
import Shipment from '~/types/Shipment';
import Transaction, { TransactionStatus } from '~/types/Transaction';

const hasRatingErrors = (
  order: Order,
  batchEntry: Maybe<BatchEntryExtended>,
  shipment: Maybe<Shipment>,
  transaction: Maybe<Transaction>,
  rates: Rate[],
  hasValidSenderAddress: boolean,
): {
  hasErrors: boolean;
  hasExceededPackageWeightError: boolean;
  transactionHasErrors: boolean;
  hasShipmentErrors: boolean;
  hasParcelWeightErrors: boolean;
  showRecipientAddressError: boolean;
  showSenderAddressError: boolean;
  showParcelError: boolean;
  hasPhoneNumberRequiredError: boolean;
} => {
  const showRecipientAddressError = hasRecipientAddressError(order) && !batchEntry?.addressTo;
  const showParcelError = !batchEntry?.parcel;
  const { addressFrom, addressTo } = getAddresses(order, batchEntry);
  const { hasPhoneNumberRequiredError } = getCustomsPhoneErrors({ addressFrom, addressTo });
  const isAddressFromIncomplete = !!addressFrom && isAddressIncomplete(addressFrom);
  const showSenderAddressError =
    (!hasValidSenderAddress && !addressFrom) || isAddressFromIncomplete;

  const transactionHasErrors =
    (batchEntry?.objectStatus === BatchEntryStatus.COMPLETE_TRANSACTION &&
      transaction?.status === TransactionStatus.ERROR) ||
    batchEntry?.objectStatus === BatchEntryStatus.TRANSACTION_REQUEST_FAILED;

  const hasShipmentErrors = !!shipment && shipment.messages.length > 0 && rates.length === 0;

  const hasParcelWeightErrors = batchEntry
    ? !(Number(batchEntry.parcel?.weight) > 0 && batchEntry.parcel?.weightUnit)
    : !(Number(order?.parcel?.weight) > 0 && order?.parcel?.weightUnit);

  const batchEntryErrors = batchEntry?.errors ? [...batchEntry.errors] : [];
  const orderErrors =
    batchEntry?.partialFulfillmentRank === 0 || order.partialFulfillmentRequests.length === 0
      ? [...order.errors]
      : [];
  const errors: APIError[] = batchEntryErrors.length ? batchEntryErrors : orderErrors;
  const hasExceededPackageWeightError = !!errors.find(
    (error) => error.code === 'order_weight_exceeded',
  );

  const hasErrors =
    showRecipientAddressError ||
    showParcelError ||
    showSenderAddressError ||
    transactionHasErrors ||
    hasShipmentErrors ||
    hasParcelWeightErrors ||
    hasExceededPackageWeightError ||
    hasPhoneNumberRequiredError;

  return {
    hasErrors,
    hasExceededPackageWeightError,
    transactionHasErrors,
    hasShipmentErrors,
    hasParcelWeightErrors,
    showRecipientAddressError,
    showSenderAddressError,
    showParcelError,
    hasPhoneNumberRequiredError,
  };
};

export default hasRatingErrors;
